// ── Global Search Module ─────────────────────────────────────────────
// Provides search-as-you-type in the navbar with grouped results dropdown.

import { t, getDocumentLanguage } from './i18n.ts'
import { escapeHtml } from './utils.ts'
import {
  localized_brand_path,
  localized_brand_nozzle_path,
  localized_brand_valve_path,
  localized_blog_article_path,
  localized_faq_path,
  localized_marketplace_path,
  localized_planner_path,
} from './routes'
import { api_v1_search_path } from './api-routes'

let debounceTimer: ReturnType<typeof setTimeout>
// Bumped on every keystroke. The debounce only spaces requests out; it does
// not order their responses, so a slow answer to "hu" used to overwrite the
// results for "hunter", or reopen the dropdown after the field was cleared.
let querySeq = 0

export function initSearch(): void {
  const input = document.getElementById('globalSearchInput') as HTMLInputElement
  const results = document.getElementById('searchResults')
  if (!input || !results) return

  // Debounced search on input
  input.addEventListener('input', () => {
    clearTimeout(debounceTimer)
    querySeq++
    const q = input.value.trim()
    if (q.length < 2) { results.hidden = true; return }
    debounceTimer = setTimeout(() => performSearch(q, results), 300)
  })

  // Cmd+K / Ctrl+K shortcut to focus
  document.addEventListener('keydown', (e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault()
      input.focus()
      input.select()
    }
    if (e.key === 'Escape') {
      results.hidden = true
      input.blur()
    }
  })

  // Close on click outside
  document.addEventListener('click', (e: MouseEvent) => {
    if (!(e.target as Element)?.closest('.nav-search')) {
      results.hidden = true
    }
  })
}

async function performSearch(query: string, container: HTMLElement): Promise<void> {
  const lang = getDocumentLanguage()
  const seq = querySeq

  try {
    const resp = await fetch(`${api_v1_search_path()}?q=${encodeURIComponent(query)}&lang=${lang}`, { credentials: 'include' })
    if (!resp.ok) return
    const data = await resp.json()
    if (seq !== querySeq) return
    renderResults(data, container, lang)
  } catch {
    // silently fail on network errors
  }
}

interface SearchSection {
  key: string
  label: string
  urlFn: (r: Record<string, any>) => string
}

function renderResults(data: Record<string, any[]>, container: HTMLElement, lang: string): void {
  const sections: SearchSection[] = [
    { key: 'brands', label: t('search.brands'), urlFn: (r) => localized_brand_path(lang, r.slug) },
    { key: 'nozzles', label: t('search.nozzles'), urlFn: (r) => localized_brand_nozzle_path(lang, r.brand_slug, r.name) },
    { key: 'valves', label: t('search.valves'), urlFn: (r) => localized_brand_valve_path(lang, r.brand_slug, r.name) },
    { key: 'articles', label: t('search.articles'), urlFn: (r) => localized_blog_article_path(lang, r.slug) },
    // Localized like the rest: the bare /marketplace redirects by the account
    // locale, and a guest has none, so a hit from /uk/... opened it in English.
    { key: 'marketplace', label: t('search.marketplace'), urlFn: (r) => `${localized_marketplace_path(lang)}#template-${encodeURIComponent(r.id)}` },
    // The planner loads a project only from `?project=ID` (planner/app.ts).
    { key: 'projects', label: t('search.projects'), urlFn: (r) => `${localized_planner_path(lang)}?project=${encodeURIComponent(r.id)}` },
    { key: 'faqs', label: t('search.faqs'), urlFn: (r) => `${localized_faq_path(lang)}#faq-${r.id}` },
  ]

  let html = ''
  let hasResults = false

  for (const section of sections) {
    const items = data[section.key] || []
    if (!items.length) continue
    hasResults = true
    html += `<div class="search-result-group"><span>${section.label}</span></div>`
    for (const item of items) {
      const name = item.name || item.title || item.slug
      html += `<a href="${escapeHtml(section.urlFn(item))}" class="search-result-item">${escapeHtml(name)}</a>`
    }
  }

  if (!hasResults) {
    html = `<div class="search-no-results">${t('search.noResults')}</div>`
  }

  container.innerHTML = html
  container.hidden = false
}

